/**
 * Dữ liệu mẫu và máy chủ giả của màn Cấu hình người dùng (user-service).
 *
 * Đóng vai user-service khi chưa đăng nhập Keycloak thật hoặc khi URL có
 * `?mock=`. Máy chủ giả **từ chối y như backend** — cùng luật trạng thái
 * (`modules/nguoi-dung/nghiep-vu/thao-tac.ts`), cùng `error_code` — để nhánh
 * lỗi của form chạy được khi chưa có backend: thử mời `nam.do@khachhang.vn`
 * (đã có), `ai@gmail.com` (domain ngoài tổ chức), hay gửi lại lời mời quá 5 lần.
 *
 * Có đủ nhánh xấu: email và vai trò `null` (bản ghi mồ côi), tên rất dài, người đã khoá.
 * Xác thực hai lớp: ba người có cờ bắt buộc riêng (`u-010`, `u-016`, `u-018`), nên
 * tổ chức mẫu đang ở mức "người được chọn"; `u-016` bị buộc mà chưa cài, `u-013`
 * (mồ côi) không đọc được trạng thái cài (`null`). Lưu danh sách có id lạ thì trả
 * `422` theo `#/user_ids/<i>` như user-service.
 * Lấy lại tài khoản: `u-017`, `u-022` đang bị khoá tạm (`login_locked`), `u-021` khoá tạm
 * nhưng đã DISABLED; gửi email đặt lại mật khẩu cho `Ngô Bảo Châu` (`u-020`) nhận `503`, gửi
 * quá 5 lần một người nhận `429`; đặt lại xác thực hai lớp cho chính mình nhận `403`.
 * `u-004` là tài khoản mẫu `tenant…` của trang đăng nhập — dòng "của chính mình".
 * Email nhận thư (ADR-0101): `u-004`, `u-016` nhận thư ở gmail, khác tên đăng nhập; đổi sang địa chỉ
 * đã là của người khác thì `422 EMAIL_NOT_INVITABLE`, đổi của chính mình thì `403`.
 * Phòng ban: id theo `mock/phong-ban.ts`; gán vào phòng ban không còn thì `404
 * DEPARTMENT_NOT_FOUND` như user-service.
 *
 * ⚠️ Tầng này bị xoá khi API thật về. Không quyết màu hay nhãn ở đây.
 */
import type { ApiError } from '../lib/errors';
import {
  GUI_LAI_TOI_DA_MOI_NGAY,
  guiLaiLoiMoiDuoc,
  khoaDuoc,
  moKhoaDuoc,
} from '../modules/nguoi-dung/nghiep-vu/thao-tac';
import type {
  EmailNhanThuChanged,
  InviteResent,
  InvitedUser,
  MfaRequiredUsers,
  UserInvite,
  UserPage,
  UserProfile,
  UserStatus,
  UserUpdate,
} from '../modules/nguoi-dung/types';
import { coPhongBan } from './phong-ban';

/** Domain đã xác minh của tổ chức mẫu. */
const DOMAIN_TO_CHUC = ['khachhang.vn'];

/** Hạn mức MAX_USERS của gói mẫu (STANDARD), đếm INVITED và ACTIVE. */
const TOI_DA_NGUOI_DUNG = 50;

const nguoi = (
  so: number,
  display_name: string,
  email: string | null,
  status: UserStatus,
  platform_role: UserProfile['platform_role'] = 'TENANT_USER',
  phu: { batBuoc?: boolean; daCai?: boolean | null; khoaTam?: boolean | null; nhanThu?: string } = {},
): UserProfile => {
  const ngay = String(28 - (so % 27)).padStart(2, '0');
  return {
    user_id: `u-${String(so).padStart(3, '0')}`,
    email,
    email_nhan_thu: phu.nhanThu ?? email,
    display_name,
    department_id: null,
    status,
    platform_role,
    locale: 'vi-VN',
    timezone: 'Asia/Ho_Chi_Minh',
    mfa_required: phu.batBuoc ?? false,
    mfa_configured: phu.daCai === undefined ? false : phu.daCai,
    login_locked: phu.khoaTam === undefined ? false : phu.khoaTam,
    created_at: `2026-08-${ngay}T02:00:00Z`,
    updated_at: `2026-10-${String(1 + (so % 18)).padStart(2, '0')}T03:30:00Z`,
  };
};

const DU_LIEU: UserProfile[] = [
  nguoi(4, 'Phạm Thu Hà', 'ha.pham@khachhang.vn', 'ACTIVE', 'TENANT_ADMIN', { daCai: true, nhanThu: 'phamthuha@gmail.com' }),
  nguoi(10, 'Đỗ Hoài Nam', 'nam.do@khachhang.vn', 'ACTIVE', 'TENANT_ADMIN', { batBuoc: true, daCai: true }),
  nguoi(11, 'Nguyễn Văn Bình', 'binh.nguyen@khachhang.vn', 'INVITED'),
  nguoi(
    12,
    'Tôn Nữ Thị Phương Thảo — Phòng Kế toán tổng hợp và Kiểm soát nội bộ chi nhánh Đà Nẵng',
    'phuongthao.tonnu.ketoan.tonghop@khachhang.vn',
    'ACTIVE',
  ),
  // Bản ghi mồ côi: hồ sơ còn, tài khoản Keycloak không còn — email và vai trò null.
  nguoi(13, 'Lê Quang Huy', null, 'ACTIVE', null, { daCai: null, khoaTam: null }),
  nguoi(14, 'Trần Minh Khoa', 'khoa.tran@khachhang.vn', 'DISABLED'),
  nguoi(15, 'Vũ Ngọc Lan', 'lan.vu@khachhang.vn', 'INVITED', 'TENANT_ADMIN'),
  nguoi(16, 'Hoàng Đức Anh', 'anh.hoang@khachhang.vn', 'ACTIVE', 'TENANT_USER', {
    batBuoc: true,
    nhanThu: 'hoangducanh.1990@gmail.com',
  }),
  nguoi(17, 'Bùi Thị Mai', 'mai.bui@khachhang.vn', 'ACTIVE', 'TENANT_USER', { khoaTam: true }),
  nguoi(18, 'Phan Thanh Tùng', 'tung.phan@khachhang.vn', 'ACTIVE', 'TENANT_USER', { batBuoc: true, daCai: true }),
  nguoi(19, 'Đặng Thu Trang', 'trang.dang@khachhang.vn', 'INVITED'),
  nguoi(20, 'Ngô Bảo Châu', 'chau.ngo@khachhang.vn', 'ACTIVE', 'TENANT_USER', { daCai: true }),
  // Khoá hẳn và khoá tạm cùng lúc: nhãn "Tạm khoá đăng nhập" không hiện cho tới khi mở khoá tài khoản.
  nguoi(21, 'Dương Văn Lực', 'luc.duong@khachhang.vn', 'DISABLED', 'TENANT_USER', { khoaTam: true }),
  nguoi(22, 'Lý Thị Hồng', 'hong.ly@khachhang.vn', 'ACTIVE', 'TENANT_USER', { daCai: true, khoaTam: true }),
  nguoi(23, 'Trịnh Công Sơn', 'son.trinh@khachhang.vn', 'ACTIVE'),
  nguoi(24, 'Mai Phương Linh', 'linh.mai@khachhang.vn', 'ACTIVE'),
  nguoi(25, 'Cao Minh Đức', 'duc.cao@khachhang.vn', 'ACTIVE'),
  nguoi(26, 'Hà Anh Tuấn', 'tuan.ha@khachhang.vn', 'INVITED'),
  nguoi(27, 'Kiều Thị Ngân', 'ngan.kieu@khachhang.vn', 'ACTIVE'),
  nguoi(28, 'La Văn Phúc', 'phuc.la@khachhang.vn', 'ACTIVE'),
  nguoi(29, 'Tạ Quỳnh Như', 'nhu.ta@khachhang.vn', 'ACTIVE'),
  nguoi(30, 'Chu Văn An', 'an.chu@khachhang.vn', 'ACTIVE'),
  nguoi(31, 'Lâm Gia Hân', 'han.lam@khachhang.vn', 'ACTIVE'),
  nguoi(32, 'Quách Tuấn Kiệt', 'kiet.quach@khachhang.vn', 'DISABLED'),
  nguoi(33, 'Đinh Thị Yến', 'yen.dinh@khachhang.vn', 'ACTIVE'),
];

/** Phòng ban ban đầu của từng người (id theo `mock/phong-ban.ts`); người vắng mặt chưa thuộc phòng ban nào. */
const PHONG_BAN_BAN_DAU: Record<string, string> = {
  'u-004': 'pb-bgd',
  'u-010': 'pb-ke-toan',
  'u-012': 'pb-ke-toan',
  'u-014': 'pb-thue',
  'u-016': 'pb-kd-mb',
  'u-017': 'pb-kd-mb',
  'u-018': 'pb-kd-mn',
  'u-020': 'pb-ngan-quy',
  'u-022': 'pb-cskh',
  'u-024': 'pb-bgd',
};

/** Bản sao trong phiên: thao tác ghi thấy được ngay, như API thật. */
let cacNguoi: UserProfile[] = structuredClone(DU_LIEU).map((u) => ({
  ...u,
  department_id: PHONG_BAN_BAN_DAU[u.user_id] ?? null,
}));

/** `mock/phong-ban.ts` đếm người của từng phòng ban, mọi trạng thái như user-service. */
export const soNguoiTheoPhongBan = (departmentId: string): number =>
  cacNguoi.filter((u) => u.department_id === departmentId).length;

/** `404 DEPARTMENT_NOT_FOUND` khi gán vào phòng ban không còn (ADR-0014). */
function kiemPhongBan(departmentId: string | null | undefined): void {
  if (departmentId && !coPhongBan(departmentId)) {
    throw loi('DEPARTMENT_NOT_FOUND', 404, 'Không tìm thấy phòng ban này trong tenant. Tải lại danh sách phòng ban rồi chọn lại.');
  }
}
const daGuiLai = new Map<string, number>();
const daGuiDatLai = new Map<string, number>();

/** Số email đặt lại mật khẩu mỗi ngày cho một người (ADR-0053); quá thì `429 RATE_LIMITED`. */
const DAT_LAI_TOI_DA_MOI_NGAY = 5;
/** Người mà máy chủ email "lỗi" khi gửi thư đặt lại mật khẩu (`503`). */
const EMAIL_LOI = 'u-020';
/** Người bị khoá khi còn INVITED: mở khoá thì về lại INVITED. */
const chuaKichHoat = new Set<string>();
let soMoi = 100;

const loi = (code: string, status: number, message: string, details?: unknown): ApiError => ({
  code,
  status,
  message,
  details,
});

function tim(id: string): UserProfile {
  const u = cacNguoi.find((x) => x.user_id === id);
  if (!u) {
    throw loi('NOT_FOUND', 404, 'Không tìm thấy người dùng này. Tải lại danh sách rồi thử lại.');
  }
  return u;
}

/** Địa chỉ đã là tên đăng nhập hay email nhận thư của người khác (realm cấm trùng cả hai, ADR-0101). */
const emailDaDung = (email: string, boQua?: string): boolean =>
  cacNguoi.some((x) => x.user_id !== boQua && (x.email === email || x.email_nhan_thu === email));

export const mayChuNguoiDung = {
  /** `GET /api/v1/users` — cursor là vị trí bắt đầu của trang sau. */
  danhSach({
    limit,
    cursor,
    status,
  }: {
    limit: number;
    cursor?: string;
    status?: UserStatus;
  }): UserPage {
    const loc = status ? cacNguoi.filter((x) => x.status === status) : cacNguoi;
    const tu = cursor ? Number(cursor) : 0;
    const items = structuredClone(loc.slice(tu, tu + limit));
    const tiep = tu + limit < loc.length ? String(tu + limit) : null;
    return { items, next_cursor: tiep };
  },

  /** `PATCH /api/v1/users/{id}`. */
  sua(id: string, du: UserUpdate): UserProfile {
    const u = tim(id);
    if (du.display_name !== undefined && !du.display_name.trim()) {
      throw loi('INVALID_ARGUMENT', 400, 'Dữ liệu gửi lên không hợp lệ.', [
        { pointer: '#/display_name', detail: 'Tên hiển thị không được để trống.' },
      ]);
    }
    kiemPhongBan(du.department_id);
    // Gửi lại cùng trạng thái là luỹ đẳng: khoá người đã khoá, mở khoá người không bị khoá.
    let status = u.status;
    if (du.status === 'DISABLED' && khoaDuoc(u.status)) status = 'DISABLED';
    if (du.status === 'ACTIVE' && moKhoaDuoc(u.status)) {
      // Mở khoá: về ACTIVE nếu người đó đã đặt mật khẩu, về INVITED nếu chưa (ADR-0040).
      status = chuaKichHoat.has(id) ? 'INVITED' : 'ACTIVE';
    }
    if (du.status === 'DISABLED' && u.status === 'INVITED') {
      chuaKichHoat.add(id);
    }
    const moi: UserProfile = {
      ...u,
      ...(du.display_name !== undefined ? { display_name: du.display_name.trim() } : {}),
      ...(du.mfa_required !== undefined ? { mfa_required: du.mfa_required } : {}),
      ...(du.department_id !== undefined ? { department_id: du.department_id } : {}),
      status,
      updated_at: new Date().toISOString(),
    };
    cacNguoi = cacNguoi.map((x) => (x.user_id === id ? moi : x));
    return structuredClone(moi);
  },

  /** `POST /api/v1/users/invite`. */
  moi(du: UserInvite): InvitedUser {
    const email = du.email.trim().toLowerCase();
    const nhanThu = du.email_nhan_thu?.trim().toLowerCase() || email;
    const domain = email.split('@')[1] ?? '';
    if (cacNguoi.some((x) => x.email === email)) {
      throw loi('USER_ALREADY_EXISTS', 409, `${email} đã là người dùng của tổ chức.`);
    }
    if (nhanThu !== email && emailDaDung(nhanThu)) {
      throw loi('EMAIL_NOT_INVITABLE', 422, 'Không mời được email này vào tenant.');
    }
    if (!DOMAIN_TO_CHUC.includes(domain)) {
      throw loi(
        'EMAIL_DOMAIN_NOT_IN_TENANT',
        422,
        `Domain ${domain} không thuộc domain đã xác minh của tổ chức. Chỉ mời được email @${DOMAIN_TO_CHUC.join(', @')}.`,
      );
    }
    const dangDung = cacNguoi.filter((x) => x.status !== 'DISABLED').length;
    if (dangDung >= TOI_DA_NGUOI_DUNG) {
      throw loi(
        'USER_QUOTA_EXCEEDED',
        422,
        `Tổ chức đã đủ ${TOI_DA_NGUOI_DUNG} người dùng theo gói. Khoá bớt tài khoản không dùng hoặc nâng gói.`,
      );
    }
    kiemPhongBan(du.department_id);
    soMoi += 1;
    const bayGio = new Date().toISOString();
    const moi: UserProfile = {
      user_id: `u-${soMoi}`,
      email,
      email_nhan_thu: nhanThu,
      display_name: du.display_name.trim(),
      department_id: du.department_id ?? null,
      status: 'INVITED',
      platform_role: du.platform_role,
      locale: 'vi-VN',
      timezone: 'Asia/Ho_Chi_Minh',
      mfa_required: false,
      mfa_configured: false,
      login_locked: false,
      created_at: bayGio,
      updated_at: bayGio,
    };
    cacNguoi = [moi, ...cacNguoi];
    return {
      user_id: moi.user_id,
      email,
      display_name: moi.display_name,
      platform_role: du.platform_role,
      department_id: moi.department_id,
      status: 'INVITED',
      invite_sent_at: bayGio,
      email_nhan_thu: nhanThu,
    };
  },

  /**
   * `PUT /api/v1/tenants/{id}/users/{user_id}/email-nhan-thu` (ADR-0101). `toiLa` thay cho `sub` của token:
   * đổi của chính mình thì `403`; địa chỉ đã là của người khác thì `422 EMAIL_NOT_INVITABLE`.
   */
  doiEmailNhanThu(id: string, emailMoi: string, toiLa: string | undefined): EmailNhanThuChanged {
    const u = tim(id);
    if (id === toiLa) {
      throw loi('PERMISSION_DENIED', 403, 'Không tự đổi được email nhận thư của chính mình.');
    }
    const email = emailMoi.trim().toLowerCase();
    const hienTai = u.email_nhan_thu ?? u.email;
    if (email === hienTai) {
      return { user_id: id, email_nhan_thu: email, changed: false, verify_email_sent: false };
    }
    if (emailDaDung(email, id)) {
      throw loi('EMAIL_NOT_INVITABLE', 422, 'Không mời được email này vào tenant.');
    }
    cacNguoi = cacNguoi.map((x) =>
      x.user_id === id ? { ...u, email_nhan_thu: email, updated_at: new Date().toISOString() } : x,
    );
    return { user_id: id, email_nhan_thu: email, changed: true, verify_email_sent: true };
  },

  /** `POST /api/v1/users/{id}/resend-invite`. */
  guiLai(id: string): InviteResent {
    const u = tim(id);
    if (!guiLaiLoiMoiDuoc(u.status)) {
      throw loi('USER_NOT_INVITED', 409, 'Người này đã kích hoạt tài khoản hoặc đã bị khoá, không cần gửi lại lời mời.');
    }
    const daGui = daGuiLai.get(id) ?? 0;
    if (daGui >= GUI_LAI_TOI_DA_MOI_NGAY) {
      throw loi('RATE_LIMITED', 429, 'Đã gửi lại lời mời 5 lần hôm nay cho người này. Thử lại từ 0 giờ ngày mai.');
    }
    daGuiLai.set(id, daGui + 1);
    return {
      user_id: id,
      invite_sent_at: new Date().toISOString(),
      resends_left_today: GUI_LAI_TOI_DA_MOI_NGAY - daGui - 1,
    };
  },

  /** `GET /api/v1/users/mfa-required`. */
  batBuocMfa(): MfaRequiredUsers {
    return { user_ids: cacNguoi.filter((x) => x.mfa_required).map((x) => x.user_id) };
  },

  /** `PUT /api/v1/users/mfa-required` — thay cả tập; id lạ thì `422` theo từng phần tử. */
  datBatBuocMfa(user_ids: string[]): MfaRequiredUsers {
    const loiTruong = user_ids.flatMap((id, i) =>
      cacNguoi.some((x) => x.user_id === id)
        ? []
        : [{ pointer: `#/user_ids/${i}`, detail: 'Người này không còn thuộc tổ chức. Tải lại danh sách rồi chọn lại.' }],
    );
    if (loiTruong.length > 0) {
      throw loi('INVALID_ARGUMENT', 422, 'Dữ liệu gửi lên không hợp lệ.', loiTruong);
    }
    const chon = new Set(user_ids);
    const bayGio = new Date().toISOString();
    cacNguoi = cacNguoi.map((x) =>
      x.mfa_required === chon.has(x.user_id)
        ? x
        : { ...x, mfa_required: chon.has(x.user_id), updated_at: bayGio },
    );
    return this.batBuocMfa();
  },

  /** `POST /api/v1/users/{id}/password-reset` — `202`, không thân. */
  guiDatLaiMatKhau(id: string): void {
    const u = tim(id);
    if (u.status === 'INVITED') {
      throw loi('USER_NOT_ACTIVE', 409, 'Người này chưa nhận lời mời nên chưa có mật khẩu để đặt lại. Dùng "Gửi lại lời mời".');
    }
    if (u.status === 'DISABLED') {
      throw loi('USER_NOT_ACTIVE', 409, 'Tài khoản này đang bị khoá. Mở khoá tài khoản trước khi gửi email đặt lại mật khẩu.');
    }
    if (id === EMAIL_LOI) {
      // user-service trả `503 INTERNAL` khi SMTP lỗi hoặc chưa cấu hình (ADR-0053).
      throw loi('INTERNAL', 503, 'Không gửi được email. Thử lại sau ít phút.');
    }
    const daGui = daGuiDatLai.get(id) ?? 0;
    if (daGui >= DAT_LAI_TOI_DA_MOI_NGAY) {
      throw loi('RATE_LIMITED', 429, `Đã gửi ${DAT_LAI_TOI_DA_MOI_NGAY} email đặt lại mật khẩu hôm nay cho người này.`);
    }
    daGuiDatLai.set(id, daGui + 1);
  },

  /** `DELETE /api/v1/users/{id}/login-lock` — `204`, luỹ đẳng. */
  moKhoaDangNhap(id: string): void {
    const u = tim(id);
    cacNguoi = cacNguoi.map((x) => (x.user_id === id ? { ...u, login_locked: false } : x));
  },

  /**
   * `DELETE /api/v1/users/{id}/otp` — `204`, luỹ đẳng. `toiLa` thay cho `sub`
   * của token: gọi cho chính mình thì `403 PERMISSION_DENIED` như user-service.
   */
  datLaiMfa(id: string, toiLa: string | undefined): void {
    const u = tim(id);
    if (id === toiLa) {
      throw loi('PERMISSION_DENIED', 403, 'Không đặt lại được xác thực hai lớp của chính mình. Gỡ ở Tài khoản của tôi.');
    }
    cacNguoi = cacNguoi.map((x) =>
      x.user_id === id ? { ...u, mfa_configured: false, updated_at: new Date().toISOString() } : x,
    );
  },
};
